import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaTimesCircle, FaList, FaSearch } from 'react-icons/fa';
import CustomFormGroup from '../components/CustomFormGroup';
import MessageModal from '../components/MessageModal';
import Pagination from '../components/Pagination';
import Table from '../components/Table';
import {
  approveMember, getMemberApprovalList, rejectMember
} from '../services/api/approvalService';
import '../styles/Approval.css';
import { useLoading } from "../components/LoadingContext";

const ApprovalPage = () => {
  const [members, setMembers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const limitOptions = [{label: '10', value: 10}, {label: '20', value: 20}, {label: '50', value: 50}, {label: 'All', value: 'All'}]
  const { showLoading, hideLoading } = useLoading();
  
  useEffect(() => {
    const fetchMembers = async () => {
      const { data, count } = await getMemberApprovalList(searchTerm, limit, (currentPage - 1) * limit);
      setMembers(data || []);
      setTotalCount(count || 0);
    };
    fetchMembers();
  }, [currentPage, searchTerm, limit, isModalOpen]);

  const handleApprove = async (id) => {
    showLoading();

    try {
      const result = await approveMember(id);

      if (!result.success) {
        setError(result.message);
      } else {
        setError('');
        setTitle('approved');
      }

      setIsModalOpen(true);
    } catch (err) {
      setError('Something went wrong');
      setIsModalOpen(true);
    } finally {
      hideLoading();
    }
  };

  const handleReject = async (id) => {
    showLoading();

    try {
      const result = await rejectMember(id);

      if (!result.success) {
        setError(result.message);
      } else {
        setError('');
        setTitle('rejected');
        // Remove the row immediately after the server confirms deletion.
        setMembers((currentMembers) => currentMembers.filter((member) => member.id !== id));
        setTotalCount((currentCount) => Math.max(0, currentCount - 1));
      }

      setIsModalOpen(true);
    } catch (err) {
      setError('Something went wrong');
      setIsModalOpen(true);
    } finally {
      hideLoading();
    }
  };


  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleLimitChange = (e) => {
    const value = e.target.value;
    setLimit(value === "All" ? "All" : parseInt(value));
    setCurrentPage(1);
  };

  const columns = [
    { key: 'full_name', label: 'Name' },
    { key: 'email', label: 'Email' },
    {
      key: 'created_at',
      label: 'Created Date',
      format: (value) => {
        const date = new Date(value);

        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(',', '');
      },
    },
  ];

  return (
    <div className="approval-page-container">
      <h2>Member Approval</h2>
      <div className="approval-controls">
        <CustomFormGroup
          type="select"
          options={limitOptions}
          value={limit}
          onChange={handleLimitChange}
          icon={<FaList />}
        />
        <CustomFormGroup
          type="text"
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          icon={<FaSearch />}
        />
      </div>
      <Table
        columns={columns}
        data={members}
        onApprove={handleApprove}
        onReject={handleReject}
      />
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount/limit)}
        onPageChange={(page) => setCurrentPage(page)}
      />
      {isModalOpen && (
        <MessageModal
          onClose={handleClose}
          title={error === '' ? 'Success' : 'Failed'}
          message_content={error === '' ? 'Successfully ' + title : 'Failed to approve due to ' + error}  
          icon={error === '' ? <FaCheckCircle/> : <FaTimesCircle/>}
          button_name={'Close'} 
          isSuccess={error === '' ? true : false} 
        />
      )}
    </div>
  );
};

export default ApprovalPage;
